import React, { useMemo } from 'react';
import { Platform, TouchableOpacity, useWindowDimensions } from 'react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import { Box, HStack, Text, theme } from '@squire/ui';

import { TimeSlotButton } from '@/components/booking/TimeSlotButton';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { parseDate, parseDateWithTimezone } from '@/utils/dateUtils';

import { styles } from './styles';
import type { ChooseTimeSlotGridProps, GridTimeSlot } from './types';

const AnimatedTouchableOpacity =
  Animated.createAnimatedComponent(TouchableOpacity);

export function ChooseTimeSlotGrid(props: ChooseTimeSlotGridProps) {
  const {
    availability,
    selectedTime,
    onTimeSelect,
    maxInitialSlots = 8,
    isExpanded,
    onToggleExpanded,
    testID,
    isSingleAppointment,
    timezone,
    disabled = false,
  } = props;

  const { t } = useScreenTranslations('ChooseTimeSlotGrid');
  const { width } = useWindowDimensions();

  const numColumns = useMemo(() => {
    if (Platform.OS === 'web') {
      if (width >= theme.breakpoints.tablet) return 4;
      if (width >= 500) return 3;
    }
    return 3;
  }, [width]);

  const timeSlots = useMemo(() => {
    if (!availability?.times) {
      return [];
    }

    // Filter slots that are available OR selected (for group appointments).
    // This matches WoW behavior: show available slots and also show the slot
    // that this appointment has selected (even if marked unavailable).
    return Object.entries(availability.times).reduce((acc, [period, slots]) => {
      slots.forEach((slot) => {
        if (slot.available || slot.selected) {
          const parsedTime = timezone
            ? parseDateWithTimezone(slot.time, timezone)
            : parseDate(slot.time);

          acc.push({
            time: parsedTime,
            period: period as 'morning' | 'afternoon' | 'evening',
            barberIds: slot.barberIds,
          });
        }
      });
      return acc;
    }, [] as GridTimeSlot[]);
  }, [availability, timezone]);

  const hasMoreSlots =
    !isSingleAppointment && timeSlots.length > maxInitialSlots;

  const visibleSlots =
    hasMoreSlots && !isExpanded
      ? timeSlots.slice(0, maxInitialSlots)
      : timeSlots;

  if (timeSlots.length === 0) return null;
  return (
    <HStack testID={testID} style={styles.gridContainer}>
      {visibleSlots.map((slot, index) => {
        return (
          <Animated.View
            style={styles.gridItem(numColumns)}
            key={slot.time.toISOString()}
            entering={FadeIn.delay(10 * index)}
          >
            <TimeSlotButton
              isAvailable={!disabled}
              time={slot.time}
              period={slot.period}
              isSelected={slot.time?.getTime() === selectedTime?.getTime()}
              onPress={() => onTimeSelect(slot.time, slot.barberIds?.[0])}
              testID={`time-slot-${slot.time.toISOString()}`}
            />
          </Animated.View>
        );
      })}

      {hasMoreSlots && (
        <Box style={styles.gridItem(numColumns)}>
          <AnimatedTouchableOpacity
            onPress={onToggleExpanded}
            style={styles.showAllButton}
            testID={`${testID}-toggle`}
            accessibilityRole="button"
            accessibilityLabel={
              isExpanded
                ? t('accessibility.showLessTimes')
                : t('accessibility.showAllTimes')
            }
          >
            <Text variant="m" color="textBrand">
              {isExpanded ? t('showLess') : t('showAll')}
            </Text>
          </AnimatedTouchableOpacity>
        </Box>
      )}
    </HStack>
  );
}
